import * as React from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { Baggage } from '../../../../BaggageService/types';
import {
	HandLuggageExcess,
	LargeBaggageExcessIcon,
	LargeBaggageIcon,
	LargeCarryOnIcon,
	MiddleBaggageExcessIcon,
	MiddleBaggageIcon,
	SmallBaggageExcessIcon,
	SmallBaggageIcon,
	SmallCarryOnIcon
} from '../../Icons';
import { useBaggageSize } from '../../hooks';
import { BaggageSize } from '../../types';
import { BaggageType } from '@websky/graphql';
import { useTheme } from '../../../../theme';

export interface Props {
	baggage: Baggage;
	className?: string;
	realSize: boolean;
	onlyIcon?: boolean;
	iconsMap?: typeof defaultIcons;
}

const defaultIcons = new Map<BaggageType, Map<BaggageSize, React.ReactNode>>([
	[
		BaggageType.CheckedBaggage,
		new Map([
			[BaggageSize.small, SmallBaggageIcon],
			[BaggageSize.middle, MiddleBaggageIcon],
			[BaggageSize.large, LargeBaggageIcon]
		])
	],
	[
		BaggageType.CarryOn,
		new Map([
			[BaggageSize.small, SmallCarryOnIcon],
			[BaggageSize.large, LargeCarryOnIcon]
		])
	],
	[
		BaggageType.HandLuggageExcess,
		new Map([
			[BaggageSize.small, SmallCarryOnIcon],
			[BaggageSize.large, HandLuggageExcess]
		])
	],
	[
		BaggageType.BaggageExcess,
		new Map([
			[BaggageSize.small, SmallBaggageExcessIcon],
			[BaggageSize.middle, MiddleBaggageExcessIcon],
			[BaggageSize.large, LargeBaggageExcessIcon]
		])
	]
]);

const BaggageIcon: React.FC<Props> = ({ baggage, className, realSize, onlyIcon, iconsMap = defaultIcons }) => {
	const { t } = useTranslation();
	const { BaggageIcon: css } = useTheme('Baggage');
	const size = useBaggageSize(baggage);

	const isCarryOn = baggage.type === BaggageType.CarryOn,
		isHandLuggageExcess = baggage.type === BaggageType.HandLuggageExcess;

	return (
		<div
			className={cn(css.root, className, {
				[css.checked_baggage]: baggage.type === BaggageType.CheckedBaggage,
				[css.carry_on]: isCarryOn,
				[css.hand_luggage_excess]: isHandLuggageExcess,
				[css.small]: size === BaggageSize.small,
				[css.middle]: size === BaggageSize.middle,
				[css.large]: size === BaggageSize.large,
				[css.real_size]: realSize,
				[css?.[baggage.type]]: css?.[baggage.type]
			})}
		>
			<div className={css.wrapper}>
				{iconsMap.get(baggage.type)?.get(size) ?? defaultIcons.get(baggage.type)?.get(size)}
				{!onlyIcon && (
					<div className={cn(css.size)}>
						{baggage.value.prefix && <div className={css.prefix}>{baggage.value.prefix}</div>}
						<div className={css.value}>{baggage.value.amount}</div>
						<div className={css.measurement}>{t(baggage.value.measurement)}</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default BaggageIcon;
